import React, { useId, useRef } from 'react';
import { X } from 'lucide-react';
import Layer from './Layer';
import { useOverlay } from './useOverlay';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

interface DialogProps {
  /** The heading. It names the dialog for assistive technology. */
  title: React.ReactNode;
  onClose: () => void;
  /** Panel width: 520, 720, 980 or 1100 px. */
  size?: DialogSize;
  /** False when the content has its own way out (a Cancel button) and a corner button would repeat it. */
  closeButton?: boolean;
  /** A short label above the heading, in the accent colour. */
  eyebrow?: string;
  /** Controls kept in the header, left of the close button. */
  actions?: React.ReactNode;
  /** A click on the dim area closes the dialog. */
  dismissOnBackdrop?: boolean;
  /** Takes the focus on open. Defaults to the dialog itself, so Enter on open presses nothing. */
  initialFocus?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

/**
 * The console's modal dialog (W1 · S4.1). It is drawn in a layer above the top bar, closes on Escape,
 * keeps Tab inside, and gives the focus back to the control that opened it (`useOverlay`).
 */
const Dialog: React.FC<DialogProps> = ({
  title,
  onClose,
  size = 'lg',
  closeButton = true,
  eyebrow,
  actions,
  dismissOnBackdrop = true,
  initialFocus,
  children,
}) => (
  <Layer>
    <DialogPanel
      title={title}
      onClose={onClose}
      size={size}
      closeButton={closeButton}
      eyebrow={eyebrow}
      actions={actions}
      dismissOnBackdrop={dismissOnBackdrop}
      initialFocus={initialFocus}
    >
      {children}
    </DialogPanel>
  </Layer>
);

const DialogPanel: React.FC<DialogProps> = ({
  title,
  onClose,
  size,
  closeButton,
  eyebrow,
  actions,
  dismissOnBackdrop,
  initialFocus,
  children,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // A text selection that starts inside and ends on the dim area is not a click on it.
  const pressedOnBackdrop = useRef(false);
  useOverlay({ panelRef, onClose, initialFocus });

  return (
    <div
      className="c3-overlay"
      role="presentation"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (dismissOnBackdrop && pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`c3-modal ui-dialog ui-dialog--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {eyebrow && <div className="ui-dialog-eyebrow">{eyebrow}</div>}
        <div className="ui-dialog-head">
          <h3 id={titleId}>{title}</h3>
          {(actions || closeButton) && (
            <div className="ui-dialog-tools">
              {actions}
              {closeButton && (
                <button type="button" className="ui-close" onClick={onClose} aria-label="Close">
                  <X size={16} strokeWidth={1.5} aria-hidden />
                </button>
              )}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default Dialog;
